import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import Navbar from './components/Navbar';
import AdminSidebar from './components/Sidebar';
import './App.css';
// Lazy load page components to improve initial load speed
const OperatorLogin = React.lazy(() => import('./pages/OperatorLogin'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const RateCardsPage = React.lazy(() => import('./pages/RateCardsPage')); 
const UploadPage = React.lazy(() => import('./pages/UploadPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const ClientsPage = React.lazy(() => import('./pages/ClientPage'));
const GSTRecordsPage = React.lazy(() => import('./pages/GSTRecordsPage'));
const ArchivePage = React.lazy(() => import('./pages/ArchivePage'));
const AddClientPage = React.lazy(() => import('./pages/addPage'));
const EditClientPage = React.lazy(() => import('./pages/editPage'));
const PageReview = React.lazy(() => import('./pages/PageReview'));

const OperatorRegister = React.lazy(() => import('./pages/OperatorRegister'));
const AdminRegister = React.lazy(() => import('./pages/AdminRegister'));
const LetterheadNotepad = React.lazy(() => import('./pages/CustomLetterHead'));
const OperatorsPage = React.lazy(() => import('./pages/OperatorsPage'));
const EditOperatorPage = React.lazy(() => import('./pages/EditOperatorPage'));
const ApprovedPages = React.lazy(() => import('./pages/ApprovedPages'));
const ArchiveAdminPage = React.lazy(() => import('./pages/Archiveadminpage'));


// Authentication context
export const AuthContext = React.createContext();

export const useAuth = () => {
  return React.useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const sessionTimeoutMinutes = 15; // 15 minutes timeout

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch user role from Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Set up activity tracking
  useEffect(() => {
    const resetTimeout = () => {
      setLastActivity(Date.now());
    };

    // Track user activity
    window.addEventListener('click', resetTimeout);
    window.addEventListener('keypress', resetTimeout);
    window.addEventListener('scroll', resetTimeout);
    window.addEventListener('mousemove', resetTimeout);

    // Check for timeout every minute
    const intervalId = setInterval(() => {
      const now = Date.now();
      const timeElapsed = now - lastActivity;

      if (currentUser && timeElapsed > sessionTimeoutMinutes * 60 * 1000) {
        logout();
        alert('Your session has expired due to inactivity. Please log in again.');
      }
    }, 60000); // Check every minute

    return () => {
      window.removeEventListener('click', resetTimeout);
      window.removeEventListener('keypress', resetTimeout);
      window.removeEventListener('scroll', resetTimeout);
      window.removeEventListener('mousemove', resetTimeout);
      clearInterval(intervalId);
    };
  }, [currentUser, lastActivity]);

  const logout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const value = {
    currentUser,
    userRole,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Protected Route component
const ProtectedRoute = ({ element, requiredRole }) => {
  const { currentUser, userRole } = useAuth();
  const location = useLocation();

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    // Redirect to appropriate dashboard based on role
    if (userRole === 'admin') {
      return <Navigate to="/dashboard" replace />;
    } else if (userRole === 'operator') {
      return <Navigate to="/upload" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  return element;
};

function AppLayout() {
  const location = useLocation();
  const { currentUser, userRole } = useAuth();
  const [collapsed, setCollapsed] = useState(true);

  // Define where Navbar should be shown (Operator Pages)
  const operatorRoutes = ['/upload', '/archive'];

  // Define where Sidebar should be shown (Admin Pages)
  const adminRoutes = ['/dashboard', '/gst-records', '/clients', '/adminBilling', '/letterhead', '/archiveAdmin', '/register', '/operators', '/operators/edit', '/rate-cards'];

  const isOperatorPage = operatorRoutes.includes(location.pathname);
  const isAdminPage = adminRoutes.includes(location.pathname);

  // Only show sidebar when user is logged in and on admin pages
  const showSidebar = currentUser && isAdminPage && userRole === 'admin';

  // Only show navbar when user is logged in and on operator pages
  const showNavbar = currentUser && isOperatorPage && userRole === 'operator';

  return (
    <div className="app">
      {/* Only render the sidebar when needed */}
      {showSidebar && <AdminSidebar collapsed={collapsed} setCollapsed={setCollapsed} />}

      <div className={`content ${showSidebar ? (collapsed ? 'with-sidebar sidebar-collapsed' : 'with-sidebar') : ''}`}>
        {/* Only render the navbar when needed */}
        {showNavbar && <Navbar />}

        <React.Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '85vh',
            fontSize: '1.1rem',
            color: '#0284c7',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontWeight: '500'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                border: '3px solid #e0f2fe',
                borderTop: '3px solid #0284c7',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 12px auto'
              }}></div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              Loading Page...
            </div>
          </div>
        }>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<OperatorLogin />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/register" element={<AdminRegister />} />

            {/* Protected operator routes */}
            <Route path="/upload" element={<ProtectedRoute element={<UploadPage />} requiredRole="operator" />} />
            <Route path="/archive" element={<ProtectedRoute element={<ArchivePage />} requiredRole="operator" />} />

            {/* Protected admin routes */}
            <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} requiredRole="admin" />} />
            <Route path="/gst-records" element={<ProtectedRoute element={<GSTRecordsPage />} requiredRole="admin" />} />
            <Route path="/clients" element={<ProtectedRoute element={<ClientsPage />} requiredRole="admin" />} />
            <Route path="/clients/add" element={<AddClientPage />} />
            <Route path="/clients/edit/:clientId" element={<EditClientPage />} />
            <Route path="/review/:pageId" element={<PageReview />} />
            <Route path="/approved-pages" element={<ApprovedPages />} />
            <Route path="/adminBilling" element={<ProtectedRoute element={<BillingPage />} requiredRole="admin" />} />
            <Route path="/letterhead" element={<ProtectedRoute element={<LetterheadNotepad />} requiredRole="admin" />} />
            <Route path="/archiveAdmin" element={<ProtectedRoute element={<ArchiveAdminPage />} requiredRole="admin" />} />
            <Route path="/operators" element={<ProtectedRoute element={<OperatorsPage />} requiredRole="admin" />} />
            <Route path="/operators/edit/:operatorId" element={<ProtectedRoute element={<EditOperatorPage />} requiredRole="admin" />} />
            <Route path="/rate-cards" element={<ProtectedRoute element={<RateCardsPage />} requiredRole="admin" />} />
            
            <Route path="/register" element={<OperatorRegister />} />

            {/* Default route redirects based on role if logged in, otherwise to login */}
            <Route path="/" element={
              currentUser ?
                (userRole === 'admin' ? <Navigate to="/dashboard" /> : <Navigate to="/upload" />)
                : <Navigate to="/login" />
            } />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </React.Suspense>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </Router>
  );
}

export default App;